<template lang="pug">
.content
  
  //- TOP NAV
  ed-title-nav(ref='header' :title='$t("Import Invoice Data")')
    span(slot='action')
        router-link(v-if='$root.legacySubtab' :to='"/d/Admin/Import"')
          i.fa.fa-long-arrow-left
          span &nbsp; {{ $t('Back') + ' ' + $t('to') + ' ' + $t($root.legacySubtab) }}
  
  //- PAGE
  .container-fluid
    ed-section.active(offset)

      //- STEPS
      ed-tab-nav.col-md-12.steps.uniform(
        steps wide
        ref='steps'
        @tabSelected='enterStep'
        :check='uploadFileValid'
      )
        
        //- 1. IMPORT FILE section
        ed-tab(title="Import File")
          
          ed-section.wide-section.-col-md-9.active(offset=0)

            h5 Import your invoice data file
            
            p
              | Imports will update existing or create new invoices based on looking at
              | the Invoice IDs of existing invoices in Eved.
              | Fields in the Invoice Information, Supplier Information,
              | and Items sections in the Invoices module can be added or updated with this import.
            
            p If the imported data has more than one tab, the first tab will be imported.
            p Column heading must begin on Row 1.

            p Accepted file formats are .xls and .xlsx

            ed-form.col-md-12.no-pad(:endpoint='api.env + api.$.invoice.upload' no-submit :submit-visible='false')
              ed-upload(button-label='Browse'
                        v-model='attachedFile'
                        :helptipHeader='"Error"'
                        :helptipBody='uploadFileError'
                        :error='!uploadFileValid'
                        :showHelptipIcon='false'
                        :required='requiredInvoiceUpload'
                        upload-auto
                        accept='application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                        extensions='xls,xlsx'
              )


        //- 2. MATCH COLUMNS section
        ed-tab(title="Match Columns" :endpoint='api.$.invoice.import'
          data='data'
          :payload='data.fieldsMap'
          feedback='errors'
          validate='checkMatched'
          )
          
          ed-section.col-md-12.active
            
            .col-md-6.col-md-offset-3
              p
                | Use the table below to match the Column Headers from Row 1 of
                | your uploaded file to match to your Invoice Fields in Eved.
              p
                | Required fields are indicated with an #[strong.text-danger *]. All required fields must have
                | a column matched to them to move to Step 3.
            p

            ed-form.col-md-12.-col-md-offset-1(:endpoint='api.$.invoice.validate' :submit-visible='false')
              table.table.table-bordered(v-show='data.fieldsMap && data.fieldsMap[0].FieldName !== null')
                thead
                  tr
                    th.col-md-3 #[br] Invoice Field
                    th #[br] Column Header in File
                tbody
                  tr(v-for='(field, index) in updateMatches(data.fieldsMap, data.importedColumns)'
                    :class='{ "required": field.IsRequired && !(field.Columns && field.Columns.length) }'
                  )
                    td {{ field.FieldName }} #[strong.text-danger {{ field.IsRequired ? '*' : '' }}]
                    td
                      ed-select(
                        :options='getImportedColumns(index)'
                        :value='columnName(field.Columns, field.Type === "list")'
                        :customLabel='columnName'
                        :required='field.IsRequired'
                        @select='handleMultimatch(field, $event)'
                        @open='fetchImportedColumns(data.importedColumns, index)'
                      )
                        a(slot='clear'
                          v-if='field.Columns && field.Columns.length === 1 && field.Type !== "list"'
                          href='#'
                          @click.prevent='removeMatch(field)'
                        )
                          i.fas.fa-times-circle.add-icon
                        
                        template(slot='option' slot-scope='props')
                          ed-select-options(thumbnails)
                            span(slot='row1')
                              kbd {{ props.option.Letter }}
                              | &nbsp;&nbsp;
                              span {{ props.option.Name || '[None]' }}
                      
                      .additional-match(v-if='field.Type && field.Columns')
                        .form-group(v-for='column in field.Columns')
                          .col-md-6
                            kbd.pull-left {{ column.Letter }}
                            small.col-md-10 {{ column.Name || `[None]` }}
                          small.col-md-1
                            a.pull-right(href='#' @click.prevent='removeColumn(column, field)')
                              i.fas.fa-times-circle
                          br.clear
                          br

        
        //- 3. REVIEW ERRORS section
        ed-tab(title="Review Errors" :optional='"Import corrected file"' :errors='errors')
          
          ed-section.col-md-11.active
            .col-md-6.col-md-offset-3
              p
                | The table below shows the errors in your file. Please correct
                | these errors in your file and return to step 1 to upload the
                | corrected file.
            
            p
            p
            table.table.table-bordered.col-md-12.-col-md-offset-1
              thead
                tr
                  th Row
                  th Column
                  th Data
                  th Column Header
                  th Invoice Field
                  th Error Reason
              tbody
                tr(v-for='(error, i) in errors')
                  td {{ error.Row }}
                  td {{ error.Column }}
                  td {{ error.Data }}
                  td {{ error.ColumnHeader }}
                  td {{ error.FieldName }}
                  td {{ error.ErrorReason }}

        
        //- 4. Additional information section
        ed-tab(title="Additional Info"
          :endpoint='api.$.invoice.additional'
          data='invoices'
          :payload='additionalInfo'
          payloadType='json'
          additional
          validate='checkItems'
          )
          
          ed-section.col-md-12.active
            
            .col-md-6.col-md-offset-3
              p
                | Use the table below to complete the additional information for each invoice. You can uncheck any invoices if you do not want to import them. If there are any duplicate alerts, the invoices are automatically unchecked, please revise the existing invoices and decide if you still want to import the invoice.
              p
                | If you need to add a new supplier, click
                div(v-if="true || addOutofNetworkSuppliersPermission")
                  div(ref="addNewSupplierDialogContainer")
                    a(href='#' @click.prevent='addNewSupplierClickHandler' name='add_new_supplier_link')
                      u + Add new supplier
            p

            ed-form.col-md-12.f-s-14.no-padding(:endpoint='api.$.invoice.create' :submit-visible='false')
              table.additional.table.table-bordered(v-show='data.fieldsMap && data.fieldsMap[0].FieldName !== null')
                thead
                  tr
                    th.col-md-2 #[br] Invoice ID
                    th.col-md-1 #[br] Supplier Invoice Number
                    th.col-md-2 #[br] Supplier
                    th.col-md-2 #[br] Program
                    th #[br] Item
                    th.col-md-2 #[br] Category
                    th #[br] Attachment #[p.small(style='font-weight: normal') Accepted file formats are .pdf, .xls and .xlsx]
                tbody
                  template(v-for='(invoice, i) in getInvoices'
                    
                  )
                    tr(:class='{ "required": !validItem(invoice, i) }')
                    td(:class='invoice.loading ? "loading" : ""'
                       :style='{ "border-bottom": "none", "border-top": duplicateInvoiceId(invoice, i) ? "none" : undefined }'
                    )
                      span(v-if='!duplicateInvoiceId(invoice, i)')
                        //- | {{ invoice['InvoiceID'] }}
                        span.small
                          ed-checkbox(
                            :options='[{ label: invoice["InvoiceID"], status: invoice.selected }]'
                            @input='setDetails("selected", invoice, $event, i)'
                          )
                          p(v-if='invoice.Duplicates') {{ $t('This might be a duplicate of:') }}
                            br
                            span(v-for='duplicate in (invoice.Duplicates || [{}])')
                              a(:href='duplicate.Url' target='_blank') {{ duplicate.Text }}
                              br
                      span(v-if="invoice.ZipCodeErrors")
                        span(v-for="zipCodeError in invoice.ZipCodeErrors")
                          p(class="zip-code-error") {{ zipCodeError.Text }}
                          br
                    td {{ invoice['SupplierInvoiceNumber'] }}
                    td(:style='{ "border-bottom": "none", "border-top": duplicateInvoiceId(invoice, i) ? "none" : undefined }')
                      div(v-if='!duplicateInvoiceId(invoice, i)')
                        div.small(v-if='invoice.ShowAddSupplierForm')
                          p {{ invoice.SupplierName }}
                          p {{ invoice.SupplierEmail }}
                          p {{ invoice.SupplierFirstName + ' ' + invoice.SupplierLastName }}
                          p {{ invoice.SupplierPhoneNumber }}
                          ed-button(
                            button-class='btn btn-actionbar'
                            @click='editSupplier(invoice, i)'
                          ) Edit
                          ed-modal(
                            :show='invoice.editSupplierModal'
                            :title='`Edit Supplier ${invoice.SupplierName}`'
                            validation
                            @validate='validateSupplier(invoice, i)'
                            @close='revertSupplier(invoice, i)'
                          )
                            div(slot='body')
                              ed-input(v-model='invoice.SupplierName')
                              br
                              ed-input(v-model='invoice.SupplierEmail')
                              br
                              ed-input(v-model='invoice.SupplierFirstName')
                              br
                              ed-input(v-model='invoice.SupplierLastName')
                              br
                              ed-input(v-model='invoice.SupplierPhoneNumber')
                              br

                        div(v-if='!invoice.ShowAddSupplierForm')
                          strong.text-danger * &nbsp;
                          ed-select.category(
                            :options='suppliers'
                            :value='invoice.SupplierKey'
                            :required='invoice.selected'
                            label='Text'
                            :customLabel='getSupplierLabel'
                            track-by='Key'
                            placeholder='Supplier'
                            @search="fetchSuppliers"
                            @open='fetchSuppliers()'
                            :openEndpoint='api.$.invoice.suppliers'
                            @select='changeSupplier(invoice, $event.Value, i)'
                            :key='invoice["ItemName"]'
                          )
                            template(
                              slot='option'
                              slot-scope='props'
                            )
                              ed-select-options(thumbnails)
                                div(slot='row2' class="no-wrap")
                                  span(class="option-strong")
                                    strong {{ he.decode(props.option.Value.CompanyName) }}
                                    em(class="option-em") ({{props.option.Value.Vci}})
                                  br
                                  span(v-if='props.option.Value.SupplierName' class="option-bold")
                                    | {{ he.decode(props.option.Value.SupplierName) }}
                                  span(v-if='props.option.Value.SupplierRequestedName')
                                    br
                                    i {{ props.option.Value.SupplierRequestedName }}
                                  span(v-if='props.option.Value.CountryName')
                                    br
                                    span(class="fas fa-map-marker-alt contact-icon")
                                    | &nbsp;{{ props.option.Value.City + (props.option.Value.State != '' && props.option.Value.State != null ? ' ' + props.option.Value.State : (props.option.Value.Region != '' && props.option.Value.Region != null ? ' ' + props.option.Value.Region : '')) + ' ' + props.option.Value.CountryName }}
                                  span(v-if='props.option.Value.BusinessType')
                                    br
                                    span(class="fas fa-briefcase contact-icon")
                                    | &nbsp;{{ props.option.Value.BusinessType }}
                                  span(v-if='props.option.Value.SupplierEmail')
                                    br
                                    span(class="fas fa-envelope contact-icon")
                                    | &nbsp;{{ props.option.Value.SupplierEmail }}
                          ed-card.supplier-card(v-if="invoice.SupplierContactName")
                            div(slot="body")
                              img(:src='invoice.SupplierLogo' width='40')
                              | &nbsp;
                              span(v-if='invoice.SupplierContactName' class="option-bold supplier-name")
                                strong {{ invoice.SupplierName }}
                                em(class="option-em") ({{invoice.SupplierEONumber}})
                                br
                                strong {{ he.decode(invoice.SupplierContactName) }}
                              span(v-if='invoice.SupplierRequestedName && invoice.SupplierRequestedName !== "null"')
                                br
                                i {{ invoice.SupplierRequestedName }}
                              span(v-if='invoice.SupplierCountryName')
                                br
                                span(class="fas fa-map-marker-alt contact-icon")
                                | &nbsp;{{ invoice.SupplierCity + (invoice.SupplierRegionOrState != '' && invoice.SupplierRegionOrState != null ? ' ' + invoice.SupplierRegionOrState : '') + ' ' + invoice.SupplierCountryName + ' ' + invoice.StoredSupplierZipCode }}
                              span(v-if='invoice.SupplierBusinessType')
                                br
                                span(class="fas fa-briefcase contact-icon")
                                | &nbsp;{{ invoice.SupplierBusinessType }}
                              span(v-if='invoice.SupplierEmail')
                                br
                                span(class="fas fa-envelope contact-icon")
                                | &nbsp;{{ invoice.SupplierEmail }}
                            span(slot="footer")
                              a.label.label-info(v-if='invoice.SupplierIsEvedPay') EvedPay ready
                              | &nbsp;
                              a.label.label-info(v-if='invoice.SupplierVendorType') {{ invoice.SupplierVendorType }} Supplier

                    td(:style='{ "border-bottom": "none", "border-top": duplicateInvoiceId(invoice, i) ? "none" : undefined }')
                      div(v-if='!duplicateInvoiceId(invoice, i)')
                        strong.text-danger * &nbsp;
                        ed-select.category(
                          :options='programs'
                          :value='invoice.ProgramKey'
                          :required='invoice.selected'
                          label='Text'
                          :customLabel='getProgramLabel'
                          track-by='Key'
                          placeholder='Program'
                          @search='fetchPrograms'
                          @open='fetchPrograms()'
                          :openEndpoint='api.$.invoice.list.uploadInvoicePrograms'
                          @select='changeProgram(invoice, $event.Value, i)'
                          :key='invoice["ItemName"]'
                        )
                          template(
                            slot='option'
                            slot-scope='props'
                          )
                            ed-select-options(thumbnails)
                              div(slot='row1' class="")
                                span(class="option-strong") 
                                  strong {{ he.decode(props.option.Value.ProgramName || '') }}
                                br
                                span(v-if="props.option.Value.InternalProgramId" class="details-text")
                                  | {{ he.decode(props.option.Value.InternalProgramId) }}
                    td Item Name: {{ invoice['ItemName'] }}
                      br
                      span Item Price: 
                      v-money(v-if="currency" :currency="currency") {{ invoice['ItemUnitPrice'] }}
                      br(v-if="invoice['ItemValueAddedTax(VAT)Amount'] !== 0")
                      span(v-if="invoice['ItemValueAddedTax(VAT)Amount'] !== 0") Item VAT: 
                      v-money(v-if="invoice['ItemValueAddedTax(VAT)Amount'] !== 0 && currency" :currency="currency") {{ invoice['ItemValueAddedTax(VAT)Amount'] }}
                      br(v-if="invoice['ItemSalesTaxAmount'] !== 0")
                      span(v-if="invoice['ItemSalesTaxAmount'] !== 0") Item Sales Tax: 
                      v-money(v-if="invoice['ItemSalesTaxAmount'] !== 0 && currency" :currency="currency") {{ invoice['ItemSalesTaxAmount'] }}
                    td
                      strong.text-danger * &nbsp;
                      ed-select.category(
                        :options='categories'
                        :value='invoice.ItemCategory || invoices.defaultCategory'
                        :required='invoice.selected'
                        label='Text'
                        track-by='Id'
                        placeholder='Category'
                        @open='fetchCategories(invoice["InvoiceID"])'
                        :openEndpoint='api.$.invoice.categories'
                        @hook:beforeCreate='setDetails("category", invoice, invoice.ItemCategory || invoices.defaultCategory, i)'
                        @select='setDetails("category", invoice, $event, i)'
                        :key='invoice["ItemName"]'
                      )
                    td(:style='{ "border-bottom": "none", "border-top": duplicateInvoiceId(invoice, i) ? "none" : undefined }')
                      div(v-if='!duplicateInvoiceId(invoice, i)')
                        strong(v-if='invoices.isAttachmentRequired').text-danger * &nbsp;
                        input(
                          :value='!invoice.uploadFileError ? invoice.attachment : undefined'
                          type='text'
                          style='opacity:0;width:0;padding:0;border:0'
                          :required='invoice.selected && invoices.isAttachmentRequired'
                        )
                        ed-upload.attachment(
                          :class='{ "attached": (invoice.attachment || []).length }'
                          button-label='Browse'
                          v-model='invoice.attachment'
                          :postAction='api.env + api.$.upload.uploadInvoice'
                          @input='validAttachment(invoice, i)'
                          :data='{ token: invoices.uploadToken }'
                          upload-auto
                          :helptipHeader='"Error"'
                          :helptipBody='invoice.uploadFileError'
                          :error='invoice.uploadFileError'
                          :showHelptipIcon='false'
                          accept='application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/pdf'
                          extensions='xls,xlsx,pdf'
                        )
                    tr(v-if='shouldDisplayTotal(invoice, i) && invoice.ProcessingFee')
                      td(colspan="4")
                      td(colspan="3")
                        p
                          | Processing Fee:
                          v-money(v-if="currency" :currency="currency") {{ invoice.ProcessingFee }}
                    tr(v-if='shouldDisplayTotal(invoice, i)')
                        td(colspan="4")
                        td(colspan="3")
                          p
                            strong
                              | Total Invoice Amount:
                              v-money(v-if="currency" :currency="currency") {{ totalInvoiceAmount(invoice) }}

        //- ========================================= FINALIZE section
        ed-tab(title="Finalize" button-label='Ok')
          
          ed-section.col-md-9.active
            .col-md-12.col-md-offset-4
              h3
                | Import Successfully Initiated
                | &nbsp;
                i.success.inline
              p
                | No errors were found in your file and your invoice data is being imported now.
                | You'll receive an email shortly when the import is complete and all data is updated in Eved.
                | In case any invoice is not imported, you'll receive an email notification with those as well.
              p
              p
              p

    //- BUTTONS
    ed-button.pull-right.btn.btn-primary(v-if='lastStep' :to='"/d/Admin/Import"') Ok
    ed-button.next-btn.pull-right(v-if='!lastStep' @click='proceed' :endpoint='nextEndpoint' :disabled='!uploadFileValid || !attachedFile') {{ $t(nextLabel) }}
    ed-button.pull-right.btn.btn-secondary(v-if='!lastStep' :to='"/d/Admin/Import"') {{ $t('Cancel') }}

    //- MODALS
    ed-modal(
      :show='notAllRequiredMatched' 
      @close='notAllRequiredMatched = false'
      title='Check Unmatched Column Headers'
      subtitle='Please match all required Invoice Fields to Column Headers in order to move on to the next step.'
      primaryButton='Ok'
      secondaryButton='false'
    )
    ed-modal(
      :show='notAllMatched' 
      @close='notAllMatched = false'
      @confirm='skipMatch'
      title='Check Unmatched Column Headers'
      subtitle='Not all of your Column Headers are matched to Invoice Fields. Do you still want to continue to the next step?'
      primaryButton='Yes'
      secondaryButton='No'
    )

</template>

<script>

import Vue from 'vue'
import { Eved, EventBus } from '@/event-bus'
import wizardMixin from '@/core/components/edTabNav/wizardMixin'
import he from 'he'
// import customTypes from './types.json'

import InviteSupplier from '../I2P/dialogs/InviteSupplierDialog'

export default {
  name: 'ImportInvoices',

  mixins: [wizardMixin],

  watch: {
    attachedFile(newVal) {
      this.requiredInvoiceUpload = !(newVal && newVal.length > 0 && this.uploadFileValid)
    }
  },
  methods: {
    /**
     * Updates matched column/columns list
     */
    handleMultimatch(field, value) {
      if (field.Type === 'list') {
        field.Columns = field.Columns || []
        field.Columns.push(value)
      } else {
        field.Columns = [value]
      }

      this.$forceUpdate()
    },

    /**
     * Removes 1:1 match
     */
    removeMatch(field) {
      field.Columns = []

      this.$forceUpdate()
    },

    /**
     * Removes 1:* match
     */
    removeColumn(column, field) {
      field && field.Columns.splice(field.Columns.indexOf(column), 1)

      this.$forceUpdate()
    },

    /**
     * Gets categories list
     */
    fetchCategories(invoiceId) {
      let invoice = this.invoices.invoices.filter(i => i['InvoiceID'] === invoiceId && i['ProgramID'] > 0)[0]
      let programId = (invoice || {})['ProgramID'] || 0
      this.api(this.api.$.invoice.categories + '?programId=' + programId).then(r => {
        this.categories = r.data || []
      })
    },

    /**
     * Gets suppliers list
     */
    fetchSuppliers(query) {
      this.api.forms.post(this.api.$.invoice.suppliers, {
        q: query || ''
      }).then(r => {
        if (r && r.data && r.data.Data) {
          this.suppliers = JSON.parse(r.data.Data)
        }
      })
    },

    /**
     * Gets suppliers list
     */
    fetchPrograms(query) {
      this.api.forms.post(this.api.$.invoice.list.uploadInvoicePrograms, { q: query || '', onlyI2PInvoices: true }).then(r => {
        if (r && r.data && r.data.Data) {
          var parsedPrograms = this.splitProgramDetails(r.data.Data)
          this.programs = parsedPrograms
        }
      })
    },

    splitProgramDetails(programsWithDetails) {
      var newPrograms = []

      programsWithDetails.map(program => {
        var elements = program.Value.split('##')
        var newProgram = {
          Key: program.Key,
          Value: {
            ProgramName: elements[0],
            InternalProgramId: elements[1] ? elements[1] : ''
          }
        }
        newPrograms.push(newProgram)
      })

      return newPrograms
    },

    /**
     * Checks if required/all fields are matched
     */
    checkMatched() {
      if (this.data.fieldsMap[0].FieldName !== null) {
        const allRequiredMatched = this.data.fieldsMap.filter(field => field.IsRequired && !(field.Columns && field.Columns.length)).length === 0
        const allMatched = this.canSkip || this.data.fieldsMap.filter(field => !field.IsRequired && !(field.Columns && field.Columns.length)).length === 0

        this.notAllRequiredMatched = !allRequiredMatched
        this.notAllMatched = allRequiredMatched && !allMatched

        if (!allRequiredMatched) {
          EventBus.$emit(Eved.INVOICE.IMPORT.INVALID)
        }

        return allRequiredMatched && allMatched
      }
    },

    /**
     * Can proceed to next step if not all not required are matched
     */
    skipMatch() {
      this.canSkip = true
      this.proceed()
      this.canSkip = false
    },

    /**
     * Callback after each step
     */
    afterEnterStep(step) {
      // Activate Admin menu
      global.Page.showTabs('Admin', 'Import', '#d', null)

      // reset any pending data
      this.$store.state.pendingData = null

      if (step) {
        EventBus.$emit(Eved.INVOICE.IMPORT.ENTERED_STEP, step)
      }
    },

    addNewSupplierClickHandler() {
      this._openInviteSupplierModalDialog('add')
    },

    _openInviteSupplierModalDialog(operation) {
      var ComponentClass = Vue.extend(InviteSupplier)
      var instance = new ComponentClass({
        propsData: {
          type: 'primary',
          model: null,
          invoiceId: this.getInvoiceId,
          eventHub: EventBus,
          operation: operation
        }
      })
      instance.$slots.default = []
      instance.$mount()
      this.$refs.addNewSupplierDialogContainer.appendChild(instance.$el)
    }
  },

  created() {
    this.importedColumns = []
    this.he = he
  },

  computed: {
    getInvoices() {
      this.invoices.invoices.forEach((invoice, i) => {
        invoice.selected = typeof invoice.selected === 'undefined' ? !invoice.Duplicates : invoice.selected
        invoice.selected = typeof invoice.selected !== 'undefined' && (this.duplicateInvoiceId(invoice, i) ? (this.invoices.invoices[i - 1] || {}).selected : invoice.selected)
        return invoice
      })
      return (this.invoices.invoices || [])
    },

    additionalInfo() {
      const o = [...this.getInvoices].filter(this.invoiceToImport).map(invoice => {
        const i = {...invoice}
        const uploadedFile = ((i.attachment || [])[0] || {}).response
        if (typeof uploadedFile === 'string') {
          i.attachment = JSON.parse(uploadedFile)
        }
        return i
      })
      return JSON.stringify(o)
    },

    currency() {
      return this.getInvoices[0].Currency
    }
  },

  data () {
    return {
      canSkip: false,
      notAllMatched: false,
      notAllRequiredMatched: false,
      supplierCopy: {},

      data: {
        fieldsMap: [
          {
            FieldName: null,
            IsRequired: true,
            Columns: []
          }
        ],
        importedColumns: []
      },

      errors: null,
      invoices: { invoices: [{ selected: true }], defaultCategory: {} },
      categories: [],
      suppliers: [],
      programs: [],
      requiredInvoiceUpload: true,

      validItem: (invoice, i) => {
        const hasCategory = invoice.category
        const hasAttachment = (invoice.attachment || []).length
        const hasToHaveAttachment = !this.duplicateInvoiceId(invoice, i)

        return hasCategory && (hasToHaveAttachment ? hasAttachment && !invoice.uploadFileError : true) || !invoice.selected
      },

      validAttachment: (invoice, i) => {
        let message = ''// 'Nothing selected'

        if (invoice.attachment && invoice.attachment[0]) {
          const file = invoice.attachment[0]
          const error = file.error

          switch (error) {
            case 'extension':
              message = 'Wrong format'
              break
            case 'denied':
              message = 'Server could not be reached'
              break
          }

          invoice.uploadFileError = message
          // this.setDetails('uploadFileError', invoice, message, i)
          return
        }

        invoice.uploadFileError = ''
        // this.setDetails('uploadFileError', invoice, '', i)
      },

      duplicateInvoiceId: (invoice, i) => {
        if (i === 0) return false
        return (this.invoices.invoices[i - 1] || {})['InvoiceID'] === invoice['InvoiceID']
      },

      shouldDisplayTotal: (invoice, i) => {
        return (this.invoices.invoices[i + 1] || {})['InvoiceID'] !== invoice['InvoiceID']
      },

      invoiceToImport: (invoice) => {
        return invoice.selected
      },

      /**
       * Column match label
       */
      columnName(option, multiple) {
        if (!option) return
        if (option.length === 0) return ''

        //
        if (multiple === false) {
          return option
        }

        // 1:* match
        if (multiple) return option && option.length + ' selected'

        // 1:1 match - [LETTER] NAME
        return option && typeof option === 'string'
                ? option
                : `[${option.Letter || option[0] && option[0].Letter}] ${option.Name || option[0] && option[0].Name || '[None]'}`
      },

      setDetails(field, invoice, value, index) {
        invoice[field] = value
        this.$set(this.invoices.invoices, index, invoice)
      },

      getProgramLabel(option) {
        if (!option.Value) return 'N/A'
        return option.Value.ProgramName + (option.Value.InternalProgramId ? ' ' + option.Value.InternalProgramId : '')
      },

      changeProgram(invoice, value, index) {
        this.setDetails('loading', invoice, true, index)
        this.setDetails('ProgramID', invoice, value.InternalProgramId.replace('(', '').replace(')', ''), index)
        this.setDetails('ProgramName', invoice, value.ProgramName, index)
        this.api.forms.post(this.api.$.invoice.getProcessingFeeByProgram, { invoices: JSON.stringify(this.invoices.invoices.filter(i => i['InvoiceID'] === invoice['InvoiceID'])) }).then(r => {
          if (r.data) {
            this.setDetails('ProcessingFee', invoice, r.data.ProcessingFee, index)
          }
          this.setDetails('loading', invoice, false, index)
        })
      },

      getSupplierLabel(option) {
        if (!option.Value) return 'N/A'
        return option.Value.CompanyName + (option.Value.Vci ? '(' + option.Value.Vci + ')' : '')
      },

      changeSupplier(invoice, value, index) {
        this.setDetails('loading', invoice, true, index)
        this.setDetails('SupplierEONumber', invoice, `${value.Vci}`, index)
        this.setDetails('SupplierID', invoice, '', index)
        this.setDetails('SupplierName', invoice, `${value.CompanyName}`, index)
        this.setDetails('SupplierContactName', invoice, `${value.SupplierName}`, index)
        this.setDetails('SupplierEmail', invoice, `${value.SupplierEmail}`, index)
        this.setDetails('SupplierIsEvedPay', invoice, `${value.IsEvedPay}`, index)
        this.setDetails('SupplierVendorType', invoice, `${value.VendorType}`, index)
        this.setDetails('SupplierLogo', invoice, `${value.Logo}`, index)
        this.setDetails('SupplierCountryName', invoice, `${value.CountryName}`, index)
        this.setDetails('SupplierCity', invoice, `${value.City}`, index)
        this.setDetails('StoredSupplierZipCode', invoice, `${value.SupplierZIP}`, index)
        this.setDetails('SupplierRegionOrState', invoice, `${value.State || value.Region}`, index)
        this.setDetails('SupplierBusinessType', invoice, `${value.BusinessType}`, index)
        this.setDetails('SupplierRequestedName', invoice, `${value.SupplierRequestedName}`, index)
        this.api.forms.post(this.api.$.invoice.validateChangeSupplierAtImport, { invoices: JSON.stringify(this.invoices.invoices.filter(i => i['InvoiceID'] === invoice['InvoiceID'])) }).then(r => {
          if (r.data) {
            if (r.data.Duplicates.length) {
              this.setDetails('Duplicates', invoice, r.data.Duplicates, index)
            }
            this.setDetails('ProcessingFee', invoice, r.data.ProcessingFee, index)
            this.setDetails('ZipCodeErrors', invoice, r.data.ZipCodeErrors, index)
          }
          this.setDetails('loading', invoice, false, index)
        })
      },

      editSupplier(invoice, index) {
        const { SupplierName, SupplierEmail, SupplierFirstName, SupplierLastName, SupplierPhoneNumber } = invoice
        this.supplierCopy = { SupplierName, SupplierEmail, SupplierFirstName, SupplierLastName, SupplierPhoneNumber }
        this.setDetails('editSupplierModal', invoice, true, index)
      },

      validateSupplier(invoice, index) {
        this.setDetails('editSupplierModal', invoice, false, index)
      },

      revertSupplier(invoice, index) {
        invoice.SupplierName = this.supplierCopy.SupplierName
        invoice.SupplierEmail = this.supplierCopy.SupplierEmail
        invoice.SupplierFirstName = this.supplierCopy.SupplierFirstName
        invoice.SupplierLastName = this.supplierCopy.SupplierLastName
        invoice.SupplierPhoneNumber = this.supplierCopy.SupplierPhoneNumber
        this.setDetails('editSupplierModal', invoice, false, index)
      },

      /**
       * Enables/disables unselected/selected columns
       */
      updateMatches(fields, columns) {
        if (!columns) return fields || []
        if (!columns.length) return fields

        const matchedFields = fields
          .map(field => field.Columns)
          .reduce((fields, columns) => (fields || []).concat(columns))
          .filter(columns => columns)
          .map(column => column.Letter)

        columns = columns.map(column => {
          if (~matchedFields.indexOf(column.Letter)) {
            column.$isDisabled = true
          } else {
            delete column.$isDisabled
          }

          return column
        })

        return fields
      },

      /**
       * Only inject options @open dropdown
       * to avoid performance bottlenecks
       * when rendering large sets
       */
      fetchImportedColumns(columns, index) {
        this.$set(this.importedColumns, index, columns)
        this.$forceUpdate()
      },
      getImportedColumns(index) {
        return this.importedColumns[index] || []
      },

      totalInvoiceAmount(invoice) {
        return this.getInvoices.filter(i => i.InvoiceID === invoice.InvoiceID).map(l => l.ItemTotalAmountWithTaxes).reduce((a, b) => a + b, 0)
      }
    }
  }
}
</script>

<style scoped lang='scss'>
@import "~@/assets/sass/_variables.scss";

.content.module >>> {
  .dawn-modal-container .page-section-main {
    width: 100%;
  }
}
.container-fluid {
  max-width: 100%;
}

.next-btn {
  &.loading {
    pointer-events: none;
    opacity: 0.5;
  }
}

.table:not(.additional) td:first-child {
  vertical-align: top;
  padding-top: 0.75em;
}

kbd {
  display: inline-block;
  margin-left: -12px;
  min-width: 25px;
  text-align: center;
  line-height: 1.1em;

  @include theme {
  background: v(secondary-01);
  }
}

.content .table >>> {
  .file-uploads > .btn-primary {
    margin-top: 0;
    margin-bottom: 0;
  }
  .attachment.loading:before {
    margin-top: -15px;
  }
  .attachment.attached .file-uploads {
    display: block;
    margin-bottom: -17px;
    > .btn-primary {
      display: none;
    }
  }
  
  .attachment.attached .helptip-wrapper {
      margin-left: 200px;
      top: -5px;
    }
  .attachment.attached .error-message {
      max-width: 200px;
    }
  
}

.no-padding {
  padding: 0;
}

.f-s-14 {
  font-size: 14px;
}

.table .form-group {
  margin-bottom: 0;
}
.category.form-group,
.attachment.form-group {
  display: inline-block;
  width: 90%;
}

.additional-match {
  > .form-group {
    float: left;
    width: 100%;
    margin-top: 5px;
    margin-bottom: 5px;

    @include theme {
    border-bottom: 1px solid v(background-light);
    }
  }
}

.category >>> .dawnSelect__content-wrapper {
  width: 400px;
}

.supplier-card.dawn-card {
  margin-left: 13px;
  margin-right: 18px;
  margin-top: 2px;
  margin-bottom: 0;
  min-height: auto;
  >>> {
    .dawn-card-top, .dawn-card-header {
      display: none;
    }
  }
}

.table.additional {
  td {
    padding: 4px;
    border: 1px solid #f2f2f2;
  }
}

.zip-code-error {
  color: red;
}

.supplier-name {
  line-height: 1em;
  display: inline-block;
  max-width: 80%;
  vertical-align: top;
}

</style>



// WEBPACK FOOTER //
// src/components/_modules/Import/invoices.vue